import { MediaItem } from '../../../app/services/mediaItems';
import { formatTimecode } from '../../../utils/timecode';
import { canonicalMediaKey, MaSequence, stripExtension } from './maTypes';
import { resolveMaDisplayName } from './parseMaDisplayNames';

/**
 * MA シーケンスモデルからの CSV 生成。
 *
 * - serializeMaSequenceCsv: 保存（新テイク）用。インポート可能な同一フォーマット
 * - serializeMaTimeChartCsv: タイムチャート書き出し用。人が読む確認資料フォーマット
 */

/** カンマ・引用符・改行を含むフィールドを RFC4180 形式でクォートする */
function csvField(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** CSV テキスト → ダウンロード用 Blob（Excel 対応のため UTF-8 BOM 付与） */
export function csvTextToBlob(text: string): Blob {
  return new Blob(['\uFEFF' + text], { type: 'text/csv;charset=utf-8' });
}

/** gain 値の文字列化（小数第 2 位まで、末尾ゼロ除去） */
function formatGain(db: number): string {
  return String(Math.round(db * 100) / 100);
}

export type MaTimeFormat = 'frames' | 'timecode';

/** 秒 → 時刻セル文字列（絶対フレーム整数 or M:SS.FF）のフォーマッタを作る */
function makeTimeFormatter(fps: number, timeFormat: MaTimeFormat) {
  return (seconds: number): string => {
    const safe = Math.max(0, seconds);
    if (timeFormat === 'timecode') {
      return formatTimecode(safe, { fps, withFrames: true });
    }
    return String(Math.round(safe * fps));
  };
}

// ===== 保存（新テイク）用 CSV =====

/**
 * タイムライン編集の保存用 CSV を再生成する（インポートと同一フォーマット）。
 *
 * 仕様:
 * - end は明示指定（hasExplicitEnd / 編集でのトリム）がある行のみ出力
 * - 本編動画が複数区間（baseVideoSpans。RPP の不連続分割由来）のセグメントは
 *   区間ごとに動画行を出力し、中間行へ end を明示する
 *   （再パース時のリンクで隣接行が 1 セグメントへ統合される）
 * - track は解決済みの値を常に明示出力する（編集後の WYSIWYG を保証）。
 *   トラック名指定（RPP の Render Track List 等）はそのまま名前を出力し、
 *   ラウンドトリップを保証する
 * - resolveItem で `_a` / `_b` バリアント切替を全行に適用できる
 * - オプションスロット（`(x)` / `[x]`）は構造ごと書き出す:
 *   スロット由来クリップは行として出力せず、`(名前=選択候補)` マーカー行と
 *   本編末尾のパレット領域（`[名前]` 見出し + 全候補行）で表現する。
 *   再パースで同じスロット構造・選択状態が復元される（ラウンドトリップ）
 *
 * 注意: 元 CSV のコメント行・リンク失敗行は保持されない（モデルからの再生成）。
 * オプション由来クリップへの個別編集（ナッジ等）は保存されない（候補の
 * テンプレート配置が正）。保存は常に「新テイク」として行われるため、
 * 元 CSV / RPP はテイク履歴に残る。
 */

export interface SerializeMaCsvOptions {
  /** `_a`/`_b` バリアント切替の適用（既定: 差し替えなし） */
  resolveItem?: (_item: MediaItem) => MediaItem;
  /** オプションスロットの選択（スロットキー → 候補キー。未指定は既定候補） */
  optionSelections?: Record<string, string>;
}

/** パレット領域に書き出す候補行（絶対秒に再配置済み） */
interface PaletteRow {
  media: string;
  start: number;
  end: number | null;
  /** ソース開始オフセット（候補動画の SOFFS 由来。音声行は null） */
  offset: number | null;
  track: number | string | null;
  gainDb: number | null;
  /** クリップ表示色（#rrggbb。動画行は null） */
  color: string | null;
}

/** `[x]` セクション 1 つぶんの書き出し計画（同名スロットで共有） */
interface PaletteSection {
  name: string;
  startSeconds: number;
  rows: PaletteRow[];
  /** 候補キー → この CSV 上で再パース時に得られる候補キー */
  writtenKeyByChoiceKey: Map<string, string>;
}

export function serializeMaSequenceCsv(
  sequence: MaSequence,
  options: SerializeMaCsvOptions = {},
): string {
  const { resolveItem = (item: MediaItem) => item, optionSelections = {} } = options;
  const formatTime = makeTimeFormatter(sequence.fps, 'frames');

  const lines: string[] = [];

  // ルーラーレーン / ビデオトラック名のメタコメント（parseMaCsv が読み戻す）。
  // トラックの表示順・レーン色フォールバック・MOV ラベルを保存後も維持する
  const rulerLanes = sequence.rulerLanes ?? [];
  if (rulerLanes.length > 0) {
    const lanesJson = JSON.stringify(
      rulerLanes.map(lane => ({ index: lane.index, name: lane.name, color: lane.colorHex })),
    );
    lines.push(`# lanes: ${lanesJson}`);
  }
  if (sequence.videoTrackName) {
    lines.push(`# videotrack: ${JSON.stringify(sequence.videoTrackName)}`);
  }

  lines.push('media,start,end,offset,track,gain,color');

  const pushRow = (
    media: string,
    start: number,
    end: number | null,
    offset: number | null,
    track: number | string | null,
    gainDb: number | null,
    color: string | null = null,
  ) => {
    lines.push(
      [
        csvField(media),
        formatTime(start),
        end != null ? formatTime(end) : '',
        offset != null ? formatTime(offset) : '',
        track != null ? csvField(String(track)) : '',
        gainDb != null && gainDb !== 0 ? formatGain(gainDb) : '',
        color ?? '',
      ].join(','),
    );
  };

  const optionSlots = sequence.optionSlots;
  const slotKeySet = new Set(optionSlots.map(s => s.key));

  // ---- オプションパレットの事前レイアウト ----
  // 本編のあらゆる開始位置より後ろの整数秒からセクションを並べる。
  // 候補行の media は解決済みファイル名で書くため、再パース時の候補キー
  // （= 先頭行の拡張子なし名 + 重複連番）をここで計算しておき、
  // スロットマーカーの `=選択` に使う
  const sectionByName = new Map<string, PaletteSection>();
  if (optionSlots.length > 0) {
    let maxSeconds = 0;
    sequence.segments.forEach(seg => {
      maxSeconds = Math.max(
        maxSeconds,
        seg.sequenceStartSeconds + Math.max(seg.videoDisplaySeconds ?? 0, seg.clipsEndSeconds),
      );
    });
    optionSlots.forEach(slot => {
      const segStart = sequence.segments[slot.segmentIndex]?.sequenceStartSeconds ?? 0;
      maxSeconds = Math.max(maxSeconds, segStart + slot.startSeconds);
    });
    let cursor = Math.floor(maxSeconds) + 1;

    optionSlots.forEach(slot => {
      const nameKey = canonicalMediaKey(slot.name);
      if (sectionByName.has(nameKey)) return; // 同名スロットはセクションを共有
      const section: PaletteSection = {
        name: slot.name,
        startSeconds: cursor,
        rows: [],
        writtenKeyByChoiceKey: new Map(),
      };
      const labelCount = new Map<string, number>();
      slot.choices.forEach(choice => {
        // 再パース時の候補キー = クラスタ先頭行の拡張子なし名。音声行を動画行より
        // 先に書くため、ラベルは先頭の音声クリップ（動画のみ候補は動画名）
        const labelItem = choice.clips[0]?.audioItem ?? choice.videoItem;
        if (!labelItem) return; // 空候補（リンク段階で除外済みのため通常到達しない）
        const label = stripExtension(resolveItem(labelItem).file_name);
        const count = (labelCount.get(label) ?? 0) + 1;
        labelCount.set(label, count);
        section.writtenKeyByChoiceKey.set(choice.key, count === 1 ? label : `${label}~${count}`);

        // 1 候補 = 1 クラスタ（同一開始バンドル）。スロット相対の並びを保って配置
        let clusterSpan = 0;
        choice.clips.forEach(clip => {
          const rel = clip.startSeconds - slot.startSeconds;
          clusterSpan = Math.max(clusterSpan, rel + (clip.durationSeconds ?? 0));
          const start = cursor + rel;
          section.rows.push({
            media: resolveItem(clip.audioItem).file_name,
            start,
            end:
              clip.hasExplicitEnd && clip.durationSeconds != null
                ? start + clip.durationSeconds
                : null,
            offset: null,
            track: clip.trackName ?? (clip.track > 0 ? clip.track : null),
            gainDb: clip.gainDb,
            color: clip.color ?? null,
          });
        });
        // 候補の差し替え動画はクラスタ先頭位置に配置。
        // end にはコンテンツ尺（videoDurationSeconds。スロット起点）を書き、
        // 再パースで表示尺の延長（resolvedDisplaySeconds）が復元される。
        // offset はスロット位置のファイル内時刻（videoOffsetSeconds）を保存し、
        // 再パースで候補動画の表示区間のファイル時刻が復元される。
        // 音声行の後に書くことで再パース時のクラスタ先頭 = ラベルが音声のまま保たれ、
        // 動画行はリンク時に file_type で候補動画として解決される）
        if (choice.videoItem) {
          const videoDuration =
            choice.videoDurationSeconds != null && choice.videoDurationSeconds > 0
              ? choice.videoDurationSeconds
              : null;
          clusterSpan = Math.max(clusterSpan, videoDuration ?? 0);
          section.rows.push({
            media: resolveItem(choice.videoItem).file_name,
            start: cursor,
            end: videoDuration != null ? cursor + videoDuration : null,
            offset:
              choice.videoOffsetSeconds != null && choice.videoOffsetSeconds > 0
                ? choice.videoOffsetSeconds
                : null,
            track: null,
            gainDb: null,
            color: null,
          });
        }
        // 次候補クラスタは前候補の終端より後ろ（開始位置クラスタが混ざらないよう 1 秒空ける）。
        // 時刻セルはフレーム整数へ丸めて書き出されるため、間隔の算出も同じ量子化を
        // 通す（丸め差で ceil が変わって再保存のたびにレイアウトがずれるのを防ぐ = 冪等性）
        const quantizedSpan = Math.round(clusterSpan * sequence.fps) / sequence.fps;
        cursor += Math.max(1, Math.ceil(quantizedSpan)) + 1;
      });
      sectionByName.set(nameKey, section);
    });
  }

  // セグメント（動画行）順に、動画行 → 帰属クリップ行 → スロットマーカー行の順で出力
  sequence.segments.forEach((segment, segIndex) => {
    const segStart = segment.sequenceStartSeconds;
    // 本編の動画行は本編タイムラインの動画（baseVideoItem / baseVideoSpans）を書く。
    // オプション候補による表示差し替えは `(名前=選択)` マーカーで表現され、
    // 再パース時のリンクで表示動画が復元される（ラウンドトリップ）。
    // 本編が複数区間（RPP の不連続分割由来）の場合は区間ごとに動画行を書き、
    // 中間行に end を明示する（再パース時のリンクで隣接行が 1 セグメントへ統合される）
    segment.baseVideoSpans.forEach((span, spanIndex) => {
      const spanItem = resolveItem(span.videoItem);
      const startAbs = segStart + span.startSeconds;
      const next = segment.baseVideoSpans[spanIndex + 1];
      const endAbs = next
        ? segStart + next.startSeconds
        : segment.hasExplicitEnd && segment.videoDisplaySeconds != null
          ? segStart + segment.videoDisplaySeconds
          : null;
      pushRow(
        spanItem.file_name,
        startAbs,
        endAbs,
        span.fileOffsetSeconds > 0 ? span.fileOffsetSeconds : null,
        null,
        null,
      );
    });

    // クリップは開始位置昇順（layoutSegmentClips 済みの順序を維持）
    segment.clips.forEach(clip => {
      // オプション由来クリップはスロットマーカー + パレットで表現するため行を出さない
      if (clip.optionSlotKey && slotKeySet.has(clip.optionSlotKey)) return;
      const audioItem = resolveItem(clip.audioItem);
      const startAbs = segStart + clip.startSeconds;
      pushRow(
        audioItem.file_name,
        startAbs,
        clip.hasExplicitEnd && clip.durationSeconds != null
          ? startAbs + clip.durationSeconds
          : null,
        null,
        clip.trackName ?? (clip.track > 0 ? clip.track : null),
        clip.gainDb,
        clip.color ?? null,
      );
    });

    // スロットマーカー行（選択中の候補を `=` で明示し、再パース時の既定にする）
    optionSlots
      .filter(slot => slot.segmentIndex === segIndex)
      .forEach(slot => {
        const section = sectionByName.get(canonicalMediaKey(slot.name));
        if (!section) return;
        const selectedKey = optionSelections[slot.key] ?? slot.defaultChoiceKey;
        const writtenKey =
          section.writtenKeyByChoiceKey.get(selectedKey) ??
          section.writtenKeyByChoiceKey.get(slot.defaultChoiceKey);
        pushRow(
          writtenKey ? `(${slot.name}=${writtenKey})` : `(${slot.name})`,
          segStart + slot.startSeconds,
          null,
          null,
          null,
          null,
        );
      });
  });

  // ---- パレット領域（`[x]` 見出し + 全候補行）を本編の後ろに出力 ----
  sectionByName.forEach(section => {
    pushRow(`[${section.name}]`, section.startSeconds, null, null, null, null);
    section.rows.forEach(row =>
      pushRow(row.media, row.start, row.end, row.offset, row.track, row.gainDb, row.color),
    );
  });

  return lines.join('\n') + '\n';
}

// ===== タイムチャート書き出し用 CSV =====

/**
 * タイムチャート CSV（人が読む確認資料）を生成する。
 *
 * 保存用 CSV とは別フォーマット:
 * - 1 列目（video）は所属動画のファイル名。動画自体の行は出力しない
 *   （動画の開始・終了は出力対象外）
 * - video 列は各動画ブロックの先頭行のみ出力し、同一動画の続く行は省略する
 * - label 列は表示名 CSV（sequence.displayNames）に一致した表示名
 *   （一致なしは空。テロップの `_` 区切り短縮は適用しない）
 * - オーディオの時刻は動画ごとの相対値（各動画の開始 = 0F）
 * - offset 列は動画行専用のため出力しない
 * - 再アップロード（インポート）用のフォーマットではない
 */

export interface SerializeMaTimeChartOptions {
  /** 出力対象セグメント index（null / 未指定 = 全セグメント） */
  segmentIndex?: number | null;
  /** 時刻列の形式（既定 'frames' = 絶対フレーム整数） */
  timeFormat?: MaTimeFormat;
}

export function serializeMaTimeChartCsv(
  sequence: MaSequence,
  options: SerializeMaTimeChartOptions = {},
): string {
  const { segmentIndex = null, timeFormat = 'frames' } = options;
  const formatTime = makeTimeFormatter(sequence.fps, timeFormat);

  const targets =
    segmentIndex != null
      ? sequence.segments.filter((_, i) => i === segmentIndex)
      : sequence.segments;

  const lines: string[] = ['video,media,label,start,end,track,gain'];

  targets.forEach(segment => {
    const videoName = segment.videoItem.file_name;
    // クリップは開始位置昇順（layoutSegmentClips 済みの順序を維持）
    segment.clips.forEach((clip, clipIndex) => {
      const track = clip.trackName ?? (clip.track > 0 ? clip.track : null);
      const label = resolveMaDisplayName(sequence.displayNames, clip);
      lines.push(
        [
          // 動画名はブロック先頭行のみ（時系列で並ぶため 2 行目以降は省略）
          clipIndex === 0 ? csvField(videoName) : '',
          csvField(clip.audioItem.file_name),
          label != null ? csvField(label) : '',
          formatTime(clip.startSeconds),
          clip.hasExplicitEnd && clip.durationSeconds != null
            ? formatTime(clip.startSeconds + clip.durationSeconds)
            : '',
          track != null ? csvField(String(track)) : '',
          clip.gainDb !== 0 ? formatGain(clip.gainDb) : '',
        ].join(','),
      );
    });
  });

  return lines.join('\n') + '\n';
}
